import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  BoldOutlined,
  CodeOutlined,
  FontColorsOutlined,
  ItalicOutlined,
  LinkOutlined,
  MailOutlined,
  OrderedListOutlined,
  SendOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Input,
  message,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Table,
  Tag,
  Tooltip,
} from "antd";

import dayjs from "dayjs";
import isEmpty from "../validations/is-empty";
import apiClient from "../actions/api";
import openNotification from "../components/Notifications/openNotification";
import getDateAndTime from "../untils/getDateAndTime";
import getDate from "../untils/getDate";

const { RangePicker } = DatePicker;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const COMPANY_NAME = "Lovapex Tech";

const Candidate = ({ title }) => {
  document.title = title;

  const dateFormat = "YYYY-MM-DD";
  const user = useSelector((state) => state.auth.user);
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);

  const [allData, setAllData] = useState([]);
  const [searchedData, setSearchedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewLoadingID, setReviewLoadingID] = useState("");
  const [messageLoadingID, setMessageLoadingID] = useState("");
  const [deleteLoadingID, setDeleteLoadingID] = useState("");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTemplateLoading, setEmailTemplateLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMode, setEmailMode] = useState("visual");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const emailEditorRef = React.useRef(null);
  const [searchText, setSearchText] = useState("");
  const [reviewFilter, setReviewFilter] = useState("all");
  const [messageFilter, setMessageFilter] = useState("all");
  const [searchStartDate, setSearchStartDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );

  const columns = [
    {
      title: "#",
      key: "no",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Role",
      dataIndex: ["job", "title"],
      key: "jobTitle",
      render: (text) =>
        !isEmpty(text) ? <Tag color="blue">{text}</Tag> : <></>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <Button type="link" onClick={() => handleCopy(text)}>
          {text}
        </Button>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (text) => (
        <Button type="link" onClick={() => handleCopy(text)}>
          {text}
        </Button>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Linkedin",
      dataIndex: "linkedin",
      key: "linkedin",
      render: (text) => (
        <Tooltip placement="top" title={text}>
          <Button
            type="text"
            aria-label="Copy LinkedIn URL"
            icon={<CopyOutlined />}
            disabled={isEmpty(text)}
            onClick={() => handleCopy(text, true)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Portfolio",
      dataIndex: "portfolio",
      key: "portfolio",
      render: (text) => (
        <Tooltip placement="top" title="Copy portfolio">
          <Button
            type="text"
            aria-label="Copy portfolio"
            icon={<CopyOutlined />}
            disabled={isEmpty(text)}
            onClick={() => handleCopy(text, true)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Discovery Source",
      dataIndex: "discoverySource",
      key: "discoverySource",
      render: (text, record) => {
        if (isEmpty(text)) return <></>;

        const source =
          text.toLowerCase() === "other" &&
          !isEmpty(record.discoverySourceOther)
            ? `Other: ${record.discoverySourceOther}`
            : text;

        return <Tag color="green">{source}</Tag>;
      },
    },
    {
      title: "Resume",
      dataIndex: "resume",
      key: "resume",
      render: (text) => renderDocumentActions(text, "Resume"),
    },
    {
      title: "Introduction",
      dataIndex: "introduction",
      key: "introduction",
      render: (text) => renderDocumentActions(text, "Introduction"),
    },

    {
      title: "Apply Date",
      dataIndex: "date",
      key: "date",
      render: (text) => getDateAndTime(text),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <div className="candidate-actions">
          <Tooltip placement="top" title="Create and send email">
            <Button
              type="primary"
              className="candidate-email-button"
              onClick={() => handleOpenEmail(record)}
              icon={<SendOutlined />}
            />
          </Tooltip>
          <Tooltip
            placement="top"
            title={
              record.messageSent === 1
                ? "Mark message as not sent"
                : "Mark message as sent"
            }
          >
            <Button
              type={record.messageSent === 1 ? "default" : "primary"}
              className={record.messageSent === 1 ? "message-sent-button" : ""}
              loading={messageLoadingID === record._id}
              onClick={() => handleMessageStatus(record)}
              icon={
                record.messageSent === 1 ? <UndoOutlined /> : <MailOutlined />
              }
            />
          </Tooltip>
          <Tooltip
            placement="top"
            title={
              record.status === 1 ? "Mark as unreviewed" : "Mark as reviewed"
            }
          >
            <Button
              type={record.status === 1 ? "default" : "primary"}
              className={record.status === 1 ? "reviewed-button" : ""}
              loading={reviewLoadingID === record._id}
              onClick={() => handleReviewStatus(record)}
              icon={
                record.status === 1 ? <UndoOutlined /> : <CheckCircleOutlined />
              }
            />
          </Tooltip>
          <Popconfirm
            title="Delete this candidate"
            description="Are you sure you want to delete this candidate?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleCandidateDelete(record._id)}
          >
            <Tooltip placement="top" title="Delete candidate">
              <Button
                type="primary"
                danger
                aria-label="Delete candidate"
                loading={deleteLoadingID === record._id}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  function renderDocumentActions(filename, label) {
    const disabled = isEmpty(filename);

    return (
      <div className="candidate-actions">
        <Tooltip placement="top" title={`View ${label}`}>
          <Button
            type="text"
            aria-label={`View ${label}`}
            icon={<EyeOutlined />}
            disabled={disabled}
            onClick={() => handleViewDocument(filename)}
          />
        </Tooltip>
        <Tooltip placement="top" title={`Download ${label}`}>
          <Button
            type="text"
            aria-label={`Download ${label}`}
            icon={<DownloadOutlined />}
            disabled={disabled}
            onClick={() => handleDownloadDocument(filename)}
          />
        </Tooltip>
      </div>
    );
  }

  const fallbackCopyTextToClipboard = (text, showCopiedContent = false) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        message.success(
          showCopiedContent
            ? `${text} was copied to clipboard`
            : "Copied to clipboard!",
        );
      } else {
        console.warn("Fallback: Copy command was unsuccessful");
        message.error("Failed to copy!");
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
      message.error("Failed to copy!");
    }

    document.body.removeChild(textArea);
  };

  const handleCopy = (copyText, showCopiedContent = false) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          message.success(
            showCopiedContent
              ? `${copyText} was copied to clipboard`
              : "Copied to clipboard!",
          );
        })
        .catch(() => {
          message.error("Failed to copy!");
        });
    } else {
      fallbackCopyTextToClipboard(copyText, showCopiedContent);
    }
  };

  const handleDownloadDocument = async (filename) => {
    const response = await fetch(`${API_BASE_URL}/candidates/${filename}`, {
      method: "GET",
    });

    if (!response.ok) {
      // handle error
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleViewDocument = (filename) => {
    const url = `${API_BASE_URL}/candidates/${filename}`;
    window.open(url, "_blank");
  };

  const handleReviewStatus = async (record) => {
    const nextStatus = record.status === 1 ? 0 : 1;
    setReviewLoadingID(record._id);

    await apiClient
      .post("/candidates/update-candidate-status", {
        id: record._id,
        status: nextStatus,
      })
      .then((res) => {
        if (res.data?.status === false) {
          message.error(res.data?.msg || "Could not update review status.");
          return;
        }

        setAllData((current) =>
          current.map((candidate) =>
            candidate._id === record._id
              ? { ...candidate, status: nextStatus }
              : candidate,
          ),
        );
        message.success(
          nextStatus === 1
            ? "Candidate marked as reviewed."
            : "Candidate marked as unreviewed.",
        );
      })
      .catch((err) => {
        openNotification(
          "error",
          "Update failed",
          err?.response?.data?.msg ||
            "Could not update candidate review status.",
        );
      })
      .finally(() => setReviewLoadingID(""));
  };

  const handleMessageStatus = async (record) => {
    const nextMessageSent = record.messageSent === 1 ? 0 : 1;
    setMessageLoadingID(record._id);

    await apiClient
      .post("/candidates/update-candidate-message-status", {
        id: record._id,
        messageSent: nextMessageSent,
      })
      .then((res) => {
        if (res.data?.status === false) {
          message.error(res.data?.msg || "Could not update message status.");
          return;
        }

        setAllData((current) =>
          current.map((candidate) =>
            candidate._id === record._id
              ? { ...candidate, messageSent: nextMessageSent }
              : candidate,
          ),
        );
        message.success(
          nextMessageSent === 1
            ? "Candidate marked as message sent."
            : "Candidate marked as message not sent.",
        );
      })
      .catch((err) => {
        openNotification(
          "error",
          "Update failed",
          err?.response?.data?.msg ||
            "Could not update candidate message status.",
        );
      })
      .finally(() => setMessageLoadingID(""));
  };

  const handleCandidateDelete = async (id) => {
    setDeleteLoadingID(id);

    try {
      const res = await apiClient.post("/candidates/delete-candidate", { id });

      if (res.data?.status === false) {
        message.error(res.data?.msg || "Could not delete the candidate.");
        return;
      }

      setAllData((current) =>
        current.filter((candidate) => candidate._id !== id),
      );
      message.success(res.data?.msg || "Candidate deleted successfully.");
    } catch (err) {
      const errors = err?.response?.data?.errors;

      if (!isEmpty(errors)) {
        errors.forEach((item) =>
          openNotification("error", "Delete failed", item.msg),
        );
      } else {
        openNotification(
          "error",
          "Delete failed",
          err?.response?.data?.msg || "Could not delete the candidate.",
        );
      }
    } finally {
      setDeleteLoadingID("");
    }
  };

  const replaceTemplateVariables = (value, record) => {
    const candidateName = String(record?.name || "").trim();
    const variables = {
      company_name: COMPANY_NAME,
      candidate_name: candidateName,
      candidate_first_name: candidateName.split(/\s+/)[0] || "",
      job_title: record?.job?.title || record?.title || "",
      candidate_email: record?.email || "",
      candidate_phone: record?.phone || "",
      candidate_address: record?.address || "",
      interview_date_range:
        record?.interview_date_range || record?.interviewDateRange || "",
      sender_name: user?.name || "",
      sender_title: user?.title || user?.job_title || "",
      sender_phone: user?.phone || "",
    };

    return String(value || "").replace(
      /{{\s*([a-zA-Z0-9_]+)\s*}}/g,
      (_, key) => variables[key] ?? "",
    );
  };

  const handleOpenEmail = async (record) => {
    setSelectedCandidate(record);
    setEmailSubject("");
    setEmailContent("");
    setEmailMode("visual");
    setEmailModalOpen(true);
    setEmailTemplateLoading(true);

    try {
      const response = await apiClient.get("/email-templates/get");
      const template =
        response.data?.emailTemplate ??
        response.data?.template ??
        response.data;

      if (!template?.subject || !template?.content) {
        throw new Error("No saved email template was found.");
      }

      setEmailSubject(replaceTemplateVariables(template.subject, record));
      setEmailContent(replaceTemplateVariables(template.content, record));
    } catch (err) {
      openNotification(
        "error",
        "Template unavailable",
        err?.response?.data?.message ||
          err?.response?.data?.errors?.[0]?.msg ||
          err.message ||
          "Could not load the saved email template.",
      );
    } finally {
      setEmailTemplateLoading(false);
    }
  };

  useEffect(() => {
    if (
      emailModalOpen &&
      emailMode === "visual" &&
      !emailTemplateLoading &&
      emailEditorRef.current
    ) {
      emailEditorRef.current.innerHTML = emailContent;
    }
  }, [emailModalOpen, emailMode, emailTemplateLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const runEmailCommand = (command) => {
    emailEditorRef.current?.focus();
    document.execCommand(command, false, null);
    setEmailContent(emailEditorRef.current?.innerHTML || "");
  };

  const addEmailLink = () => {
    const url = window.prompt("Enter the link URL");
    if (!url) return;
    emailEditorRef.current?.focus();
    document.execCommand("createLink", false, url);
    setEmailContent(emailEditorRef.current?.innerHTML || "");
  };

  const handleSendEmail = async () => {
    if (!selectedCandidate?.email) {
      message.warning("This candidate does not have an email address.");
      return;
    }
    if (!emailSubject.trim() || !emailContent.trim()) {
      message.warning("Email subject and content are required.");
      return;
    }

    setEmailSending(true);
    try {
      const response = await apiClient.post("/email-templates/send", {
        candidateId: selectedCandidate._id,
        to: selectedCandidate.email,
        subject: emailSubject.trim(),
        content: emailContent,
      });

      if (response.data?.status === false) {
        throw new Error(response.data?.msg || "Could not send the email.");
      }

      try {
        await apiClient.post("/candidates/update-candidate-message-status", {
          id: selectedCandidate._id,
          messageSent: 1,
        });
      } catch (statusError) {
        // Delivery succeeded, so still reflect it immediately in the table.
        console.error(
          "Could not persist candidate message status:",
          statusError,
        );
      }

      setAllData((current) =>
        current.map((candidate) =>
          candidate._id === selectedCandidate._id
            ? { ...candidate, messageSent: 1 }
            : candidate,
        ),
      );
      message.success(`Email sent to ${selectedCandidate.email}.`);
      setEmailModalOpen(false);
    } catch (err) {
      openNotification(
        "error",
        "Email not sent",
        err?.response?.data?.message ||
          err?.response?.data?.errors?.[0]?.msg ||
          err.message ||
          "Could not send the email.",
      );
    } finally {
      setEmailSending(false);
    }
  };

  const handleSearch = () => {
    setSearchText("");
    getData();
  };

  const getData = async () => {
    const originalStartTime = `${dayjs(searchStartDate).year()}-${String(
      dayjs(searchStartDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchStartDate).date()).padStart(
      2,
      "0",
    )} 00:00:00`;

    const timeStartInUTC = dayjs(originalStartTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeStartInGMTPlus9 = timeStartInUTC.tz(
      timezoneMode.timezoneMode,
      true,
    );

    const originalEndTime = `${dayjs(searchEndDate).year()}-${String(
      dayjs(searchEndDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchEndDate).date()).padStart(
      2,
      "0",
    )} 23:59:59`;
    const timeEndInUTC = dayjs(originalEndTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeEndInGMTPlus9 = timeEndInUTC.tz(timezoneMode.timezoneMode, true);

    const params = {
      searchStartDate: timeStartInGMTPlus9,
      searchEndDate: timeEndInGMTPlus9,
    };

    setLoading(true);

    await apiClient
      .post("/candidates/get-candidates", params)
      .then((res) => {
        if (!isEmpty(res.data?.result)) {
          setAllData(res.data?.result);
          setSearchedData(res.data?.result);
        } else {
          setAllData([]);
          setSearchedData([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    const filteredData = allData.filter((item) => {
      const matchesKeywords =
        !normalizedSearch ||
        `${Object.values(item).join(" ")} ${item.job?.title || ""}`
          .toLowerCase()
          .includes(normalizedSearch);
      const matchesReviewStatus =
        reviewFilter === "all" || Number(item.status || 0) === reviewFilter;
      const matchesMessageStatus =
        messageFilter === "all" ||
        Number(item.messageSent || 0) === messageFilter;

      return matchesKeywords && matchesReviewStatus && matchesMessageStatus;
    });

    setSearchedData(filteredData);
  }, [searchText, reviewFilter, messageFilter, allData]);

  useEffect(() => {
    if (user && !isEmpty(user._id)) {
      getData();
    }
  }, [user, searchStartDate, searchEndDate]);

  return (
    <div
      className={`dashboardSection management-page ${themeMode.themeMode ? "dashboard-dark" : ""}`}
    >
      <div className="dashboard-heading">
        <div>
          <h2 className="title">
            <span className="title-icon">
              <UserSwitchOutlined />
            </span>
            Candidates
          </h2>
          <p>Review candidate profiles and submitted resumes.</p>
        </div>
        <Tag className="result-badge">{searchedData.length} candidates</Tag>
      </div>
      <Card className="search-section" variant="borderless">
        <div className="filter-heading">
          <div>
            <SearchOutlined /> Search & filters
          </div>
          <span>Find candidates by profile details or date</span>
        </div>
        <div className="filter-grid management-filter-grid">
          <div className="field">
            <div className="label">Keywords</div>
            <Input
              prefix={<SearchOutlined className="input-icon" />}
              placeholder="e.g. name, email or role"
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
          </div>
          <div className="field">
            <div className="label">Application date</div>
            <RangePicker
              value={[searchStartDate, searchEndDate]}
              format={dateFormat}
              disabled={loading}
              onChange={(dates, dateStrings) => {
                if (dates) {
                  setSearchStartDate(
                    dayjs(dateStrings[0]).tz(timezoneMode.timezoneMode, true),
                  );
                  setSearchEndDate(
                    dayjs(dateStrings[1]).tz(timezoneMode.timezoneMode, true),
                  );
                } else {
                  setSearchStartDate(
                    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
                  );
                  setSearchEndDate(
                    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
                  );
                }
              }}
            />
          </div>
          <div className="field">
            <div className="label">Review status</div>
            <Select
              value={reviewFilter}
              onChange={setReviewFilter}
              options={[
                { value: "all", label: "All candidates" },
                { value: 1, label: "Reviewed" },
                { value: 0, label: "Unreviewed" },
              ]}
            />
          </div>
          <div className="field">
            <div className="label">Message status</div>
            <Select
              value={messageFilter}
              onChange={setMessageFilter}
              options={[
                { value: "all", label: "All candidates" },
                { value: 1, label: "Message sent" },
                { value: 0, label: "Message not sent" },
              ]}
            />
          </div>
          <div className="field action-field">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              Apply filters
            </Button>
          </div>
        </div>
      </Card>
      <Card className="table-section" variant="borderless">
        <div className="table-heading">
          <div>
            <h3>Candidate directory</h3>
            <span>Profiles received in the selected period</span>
          </div>
          <Tag>{searchedData.length} records</Tag>
        </div>
        <Table
          columns={columns}
          dataSource={searchedData}
          loading={loading}
          rowKey="_id"
          rowClassName={(record) => {
            if (record.messageSent === 1) return "candidate-row-message-sent";
            if (record.status === 1) return "candidate-row-reviewed";
            return "";
          }}
          size="middle"
          scroll={{ x: 1200 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `${total} candidates`,
          }}
        />
      </Card>
      <Modal
        className={`candidate-email-modal ${
          themeMode.themeMode ? "candidate-email-modal-dark" : ""
        }`}
        title={
          <div className="candidate-email-modal-title">
            <span className="candidate-email-title-icon">
              <MailOutlined />
            </span>
            <span className="candidate-email-title-copy">
              <strong>Compose email</strong>
              <small>
                {selectedCandidate?.name || "Candidate"} ·{" "}
                {selectedCandidate?.email || "No email address"}
              </small>
            </span>
          </div>
        }
        open={emailModalOpen}
        width={900}
        onCancel={() => !emailSending && setEmailModalOpen(false)}
        destroyOnHidden
        footer={[
          <Button
            key="cancel"
            disabled={emailSending}
            onClick={() => setEmailModalOpen(false)}
          >
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={emailSending}
            disabled={emailTemplateLoading || !selectedCandidate?.email}
            onClick={handleSendEmail}
          >
            Send email
          </Button>,
        ]}
      >
        <div className="candidate-email-form">
          <div className="modal-field">
            <div className="label">To</div>
            <Input value={selectedCandidate?.email || ""} disabled />
          </div>
          <div className="modal-field">
            <div className="label">Subject</div>
            <Input
              value={emailSubject}
              disabled={emailTemplateLoading}
              onChange={(event) => setEmailSubject(event.target.value)}
              placeholder="Email subject"
            />
          </div>
          <div className="candidate-email-editor-heading">
            <div className="label">Email content</div>
            <Segmented
              size="small"
              value={emailMode}
              disabled={emailTemplateLoading}
              onChange={setEmailMode}
              options={[
                {
                  label: "Visual",
                  value: "visual",
                  icon: <FontColorsOutlined />,
                },
                { label: "HTML", value: "html", icon: <CodeOutlined /> },
              ]}
            />
          </div>
          {emailMode === "visual" ? (
            <div className="candidate-email-editor">
              <div className="candidate-email-toolbar">
                {[
                  ["Bold", <BoldOutlined />, "bold"],
                  ["Italic", <ItalicOutlined />, "italic"],
                  ["Underline", <UnderlineOutlined />, "underline"],
                  [
                    "Bulleted list",
                    <UnorderedListOutlined />,
                    "insertUnorderedList",
                  ],
                  [
                    "Numbered list",
                    <OrderedListOutlined />,
                    "insertOrderedList",
                  ],
                ].map(([label, icon, command]) => (
                  <Tooltip title={label} key={command}>
                    <button
                      type="button"
                      aria-label={label}
                      disabled={emailTemplateLoading}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => runEmailCommand(command)}
                    >
                      {icon}
                    </button>
                  </Tooltip>
                ))}
                <Tooltip title="Add link">
                  <button
                    type="button"
                    aria-label="Add link"
                    disabled={emailTemplateLoading}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={addEmailLink}
                  >
                    <LinkOutlined />
                  </button>
                </Tooltip>
              </div>
              <div
                ref={emailEditorRef}
                className="candidate-email-canvas"
                contentEditable={!emailTemplateLoading}
                suppressContentEditableWarning
                onInput={(event) =>
                  setEmailContent(event.currentTarget.innerHTML)
                }
              />
            </div>
          ) : (
            <Input.TextArea
              className="candidate-email-html"
              value={emailContent}
              disabled={emailTemplateLoading}
              onChange={(event) => setEmailContent(event.target.value)}
              autoSize={{ minRows: 16, maxRows: 24 }}
              spellCheck={false}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Candidate;
